import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Search, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../api';
import { PageHeader, Card, EmptyState, TableSkeleton } from '../components/ui';

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [azione, setAzione] = useState('');
  const [esito, setEsito] = useState('');
  const [utente, setUtente] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/logs', { params: { limit: 200, azione: azione || undefined, esito: esito || undefined, utente_email: utente || undefined } })
      .then(r => { setRows(r.data.logs || []); setTotal(r.data.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [azione, esito, utente]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const fmt = (d) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="eden-page">
      <PageHeader title="Audit log" icon={ShieldCheck} subtitle={`Chi ha fatto cosa · ${total} eventi registrati`} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input value={azione} onChange={e => setAzione(e.target.value)} placeholder="Filtra per azione…" className="eden-input" style={{ paddingLeft: 34, height: 38 }} />
        </div>
        <input value={utente} onChange={e => setUtente(e.target.value)} placeholder="Email utente…" className="eden-input" style={{ height: 38, width: 'auto', minWidth: 160 }} />
        <select value={esito} onChange={e => setEsito(e.target.value)} className="eden-select" style={{ width: 'auto' }}>
          <option value="">Tutti gli esiti</option>
          <option value="success">Successo</option>
          <option value="error">Errore</option>
        </select>
      </div>

      {loading ? (
        <TableSkeleton rows={8} cols={4} />
      ) : rows.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Nessun evento" hint="Le azioni degli utenti compaiono qui." />
      ) : (
        <Card padding={0} style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 680 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1.4fr 1.2fr 1.4fr 70px', gap: 0, padding: '10px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
                {['Data', 'Utente', 'Azione', 'Risorsa', 'Esito'].map(h => (
                  <span key={h} style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</span>
                ))}
              </div>
              {rows.map((l, i) => {
                const ok = l.esito === 'success' || (l.status_code && l.status_code < 400);
                return (
                  <div key={l.id || i} style={{ display: 'grid', gridTemplateColumns: '120px 1.4fr 1.2fr 1.4fr 70px', gap: 0, padding: '9px 14px', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center', fontSize: 12.5 }}>
                    <span style={{ color: 'var(--text-dim)', fontSize: 11.5 }}>{fmt(l.creato_il)}</span>
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.utente_nome || l.utente_email || '—'}
                      {l.ruolo && <span style={{ color: 'var(--text-dim)', fontSize: 10.5, marginLeft: 5 }}>{l.ruolo}</span>}
                    </span>
                    <span style={{ color: 'var(--cyan)', fontWeight: 600, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.azione}</span>
                    <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.risorsa || ''}{l.risorsa_id ? ` · ${String(l.risorsa_id).slice(0, 8)}` : ''}</span>
                    <span>{ok ? <CheckCircle2 size={14} color="var(--green)" /> : <AlertCircle size={14} color="var(--red)" />}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
